import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintWithGlyph, HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Input, Label } from "@/components/ui/Input";
import {
  type MrpAmlChangeRequestListRow,
  type MrpAmlEntry,
  type MrpAmlListResponse,
  type MrpSkuOption,
} from "@/lib/mrp";

import { AmlEntriesTable } from "./aml/AmlEntriesTable";
import {
  AML_SHOW_INACTIVE_HINT,
  amlCpnDetailPath,
  amlChangeRequestActionLabel,
  summarizeAmlChangeRequestAction,
} from "./aml/amlEntryUtils";

const AML_PAGE_SIZE = 50;

const AML_ENTRIES_SECTION_HINT =
  `Search runs on the server with a short debounce. Results are paginated by CPN (${String(AML_PAGE_SIZE)} CPNs per page). Status is the approval lifecycle (approved vs deprecated). Active is only a listing flag: inactive approved lines still appear in shopping. With Show inactive off, only active approved lines are returned. Hyphens and underscores are ignored when matching CPN, description, category name, variant, and MPN (for example ICS-3496 matches ICS_3496). Entries are grouped by CPN; click a CPN to open its detail page. Hover a CPN for catalog category, description, and variant (same SKU hover hint as elsewhere in MRP). Each subrow shows manufacturer-specific part info when present. Use row actions to edit notes immediately or submit activate, deactivate, or delete requests. Deactivate hides a line from this list; it does not un-approve it. Manufacturer, MPN, and source are fixed after a line is approved.`;

interface DraftAml {
  cpn: string;
  manufacturer_name: string;
  mpn: string;
  item_source: string;
  notes: string;
}

const EMPTY_DRAFT: DraftAml = {
  cpn: "",
  manufacturer_name: "",
  mpn: "",
  item_source: "",
  notes: "",
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function Aml() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const hubMrpAdminAllowed = useHubModuleAdmin("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const mrpAmlResolutionTitle =
    "MRP module administrator access is required to approve or reject AML change requests.";
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 450);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [draft, setDraft] = useState<DraftAml>(EMPTY_DRAFT);
  const [notesRequestRow, setNotesRequestRow] = useState<MrpAmlEntry | null>(null);
  const [notesRequestDraft, setNotesRequestDraft] = useState("");

  const amlQuery = useQuery({
    queryKey: ["mrp", "aml", { includeInactive, q: debouncedSearch, page }],
    queryFn: () =>
      apiFetch<MrpAmlListResponse>("/api/mrp/aml", {
        query: {
          page: String(page),
          page_size: String(AML_PAGE_SIZE),
          ...(debouncedSearch.length > 0 ? { q: debouncedSearch } : {}),
          ...(includeInactive ? { include_inactive: "true" } : {}),
        },
      }),
    // Keep the prior page only when paging within the same search; clear rows when q or
    // includeInactive changes so stale CPNs cannot linger while the new search is in flight.
    placeholderData: (previousData, previousQuery) => {
      if (previousData == null || previousQuery == null) return undefined;
      const previousFilters = previousQuery.queryKey[2] as
        | { includeInactive?: boolean; q?: string }
        | undefined;
      if (previousFilters == null) return undefined;
      if (
        previousFilters.includeInactive === includeInactive &&
        previousFilters.q === debouncedSearch
      ) {
        return previousData;
      }
      return undefined;
    },
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, includeInactive]);

  const skuOptionsQuery = useQuery({
    queryKey: ["mrp", "aml-sku-options"],
    queryFn: () => apiFetch<{ skus: MrpSkuOption[] }>("/api/mrp/aml-sku-options"),
    enabled: addModalOpen,
  });

  const rows = amlQuery.data?.rows ?? [];
  const skuHoverHints = amlQuery.data?.sku_hover_hints ?? {};
  const totalCpns = amlQuery.data?.total_cpns ?? 0;
  const totalPages = amlQuery.data?.total_pages ?? 1;
  const rangeStart = totalCpns === 0 ? 0 : (page - 1) * AML_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * AML_PAGE_SIZE, totalCpns);
  const listLoading = amlQuery.isPending || (amlQuery.isFetching && rows.length === 0);

  const amlRequestsQuery = useQuery({
    queryKey: ["mrp", "aml-requests", "pending"],
    queryFn: () =>
      apiFetch<{ rows: MrpAmlChangeRequestListRow[] }>("/api/mrp/aml-requests", {
        query: { status: "pending" },
      }),
  });

  const pendingRequests = amlRequestsQuery.data?.rows ?? [];

  const createMutation = useMutation({
    mutationFn: (input: DraftAml) =>
      apiFetch("/api/mrp/aml", {
        method: "POST",
        body: {
          cpn: input.cpn.trim(),
          manufacturer_name: input.manufacturer_name.trim(),
          mpn: input.mpn.trim() || "",
          item_source: input.item_source.trim() || null,
          notes: input.notes.trim() || null,
        },
      }),
    onSuccess: async () => {
      setDraft(EMPTY_DRAFT);
      setAddModalOpen(false);
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "pending"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  useEffect(() => {
    if (!addModalOpen) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        createMutation.reset();
        setAddModalOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [addModalOpen, createMutation]);

  useEffect(() => {
    if (!notesRequestRow) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setNotesRequestRow(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notesRequestRow]);

  type AmlRequestBody = { kind: "activate" } | { kind: "deactivate" } | { kind: "delete" };

  const notesMutation = useMutation({
    mutationFn: (input: { id: number; notes: string | null }) =>
      apiFetch(`/api/mrp/aml/${input.id}/notes`, {
        method: "PATCH",
        body: { notes: input.notes },
      }),
    onSuccess: async () => {
      setNotesRequestRow(null);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  const amlRequestMutation = useMutation({
    mutationFn: (input: { id: number; body: AmlRequestBody }) =>
      apiFetch(`/api/mrp/aml/${input.id}/request`, { method: "POST", body: input.body }),
    onSuccess: async () => {
      setNotesRequestRow(null);
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "pending"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml-requests", "history"] });
    },
  });

  const approveRequestMutation = useMutation({
    mutationFn: (requestId: number) =>
      apiFetch(`/api/mrp/aml-requests/${requestId}/approve`, { method: "POST", body: {} }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "pending"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml-requests", "history"] });
    },
  });

  const rejectRequestMutation = useMutation({
    mutationFn: (input: { requestId: number; resolution_notes?: string | null }) =>
      apiFetch(`/api/mrp/aml-requests/${input.requestId}/reject`, {
        method: "POST",
        body: { resolution_notes: input.resolution_notes ?? null },
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "pending"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml-requests", "history"] });
    },
  });

  const requestBusy =
    amlRequestMutation.isPending ||
    notesMutation.isPending ||
    approveRequestMutation.isPending ||
    rejectRequestMutation.isPending;

  useEffect(() => {
    if (amlQuery.data == null) return;
    if (page > amlQuery.data.total_pages) {
      setPage(amlQuery.data.total_pages);
    }
  }, [amlQuery.data, page]);

  const emptyMessage = (() => {
    if (listLoading) return null;
    if (debouncedSearch.length > 0) {
      return "No AML entries match your search. Check spelling, turn on Show inactive if the row may be inactive, or try manufacturer or MPN.";
    }
    return "No AML entries yet. Use Add entry to create one.";
  })();

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Approved Material List</h1>
          <p className="text-sm text-slate-600">
            Data lives in <code className="rounded bg-slate-100 px-1 text-xs">public.approved_components</code> (the same
            AML as Manufacturing Operations). New rows require an existing SKU in{" "}
            <code className="rounded bg-slate-100 px-1 text-xs">public.sku</code>. Use Add entry to submit a new
            manufacturer alternate for approval; after approval, manufacturer, MPN, and source are locked. Notes can be
            edited anytime without approval. Activate, deactivate, and delete still require approval below (MRP module
            administrators only).
          </p>
        </div>
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
          <Button
            className="shrink-0 self-start"
            size="sm"
            variant="primary"
            disabled={!hubWritesAllowed}
            onClick={() => {
              createMutation.reset();
              setDraft(EMPTY_DRAFT);
              setAddModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add entry
          </Button>
        </ReadOnlyHoverLayer>
      </header>

      {addModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            createMutation.reset();
            setAddModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="aml-add-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="aml-add-title" className="text-lg font-semibold text-slate-900">
                Add AML entry
              </h2>
              <HoverHintWithGlyph hoverHint="Submits a new approved-material alternate for review. Manufacturer, MPN, and source are fixed after approval." />
            </div>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="aml-draft-cpn">CPN</Label>
                  <Input
                    id="aml-draft-cpn"
                    list="aml-cpn-options"
                    value={draft.cpn}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((current) => ({ ...current, cpn: event.target.value }))}
                    placeholder="Internal part number"
                  />
                  <datalist id="aml-cpn-options">
                    {(skuOptionsQuery.data?.skus ?? []).map((skuRow) => (
                      <option key={skuRow.sku} value={skuRow.sku}>
                        {skuRow.display_name ?? skuRow.sku}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <Label htmlFor="aml-draft-mfr">Manufacturer</Label>
                  <Input
                    id="aml-draft-mfr"
                    value={draft.manufacturer_name}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, manufacturer_name: event.target.value }))
                    }
                    placeholder="Approved manufacturer name"
                  />
                </div>
                <div>
                  <Label htmlFor="aml-draft-mpn">MPN (optional)</Label>
                  <Input
                    id="aml-draft-mpn"
                    value={draft.mpn}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((current) => ({ ...current, mpn: event.target.value }))}
                    placeholder="Manufacturer part number"
                  />
                </div>
                <div>
                  <Label htmlFor="aml-draft-item-source">Source (optional)</Label>
                  <Input
                    id="aml-draft-item-source"
                    value={draft.item_source}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, item_source: event.target.value }))
                    }
                    placeholder="Where this line is sourced from"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Source is saved with the line and cannot be changed after approval.
                  </p>
                </div>
                <div>
                  <Label htmlFor="aml-draft-notes">Notes (optional)</Label>
                  <Input
                    id="aml-draft-notes"
                    value={draft.notes}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
                    placeholder="Free-form notes only"
                  />
                </div>
              </div>
            </ReadOnlyHoverLayer>
            <div className="mt-5 flex flex-wrap gap-2">
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={
                    !hubWritesAllowed ||
                    createMutation.isPending ||
                    !draft.cpn.trim() ||
                    !draft.manufacturer_name.trim()
                  }
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    createMutation.mutate(draft);
                  }}
                >
                  {createMutation.isPending ? "Submitting…" : "Submit for approval"}
                </Button>
              </ReadOnlyHoverLayer>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  createMutation.reset();
                  setAddModalOpen(false);
                }}
              >
                Cancel
              </Button>
            </div>
            {createMutation.isError ? (
              <p className="mt-3 text-sm text-red-600">
                {createMutation.error instanceof Error ? createMutation.error.message : "Could not save."}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {notesRequestRow ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            notesMutation.reset();
            setNotesRequestRow(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="aml-notes-request-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="aml-notes-request-title" className="text-lg font-semibold text-slate-900">
                Edit notes
              </h2>
              <HoverHintWithGlyph
                hoverHint={`Notes for ${notesRequestRow.cpn}. Manufacturer, MPN, and source cannot be changed here.`}
              />
            </div>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="aml-notes-request-input">Notes</Label>
                  <Input
                    id="aml-notes-request-input"
                    value={notesRequestDraft}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setNotesRequestDraft(event.target.value)}
                    placeholder="Leave empty to clear notes"
                  />
                </div>
              </div>
            </ReadOnlyHoverLayer>
            <div className="mt-5 flex flex-wrap gap-2">
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
                <SaveChangesButton
                  size="sm"
                  variant="primary"
                  isDirty={notesRequestDraft.trim() !== (notesRequestRow.notes ?? "").trim()}
                  disabled={notesMutation.isPending || !hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    notesMutation.mutate({
                      id: notesRequestRow.id,
                      notes: notesRequestDraft.trim() || null,
                    });
                  }}
                >
                  {notesMutation.isPending ? "Saving…" : "Save notes"}
                </SaveChangesButton>
              </ReadOnlyHoverLayer>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  notesMutation.reset();
                  setNotesRequestRow(null);
                }}
              >
                Cancel
              </Button>
            </div>
            {notesMutation.isError ? (
              <p className="mt-3 text-sm text-red-600">
                {notesMutation.error instanceof Error
                  ? notesMutation.error.message
                  : "Could not save notes."}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Pending AML requests</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {amlRequestsQuery.isError ? (
            <p className="text-sm text-red-600">
              Could not load pending requests
              {amlRequestsQuery.error instanceof Error ? `: ${amlRequestsQuery.error.message}` : "."}
            </p>
          ) : amlRequestsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : pendingRequests.length === 0 ? (
            <p className="text-sm text-slate-600">No pending requests.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Requested</th>
                    <th className="px-3 py-2">Requester</th>
                    <th className="px-3 py-2">
                      <HoverHintContent hoverHint="Primary key of the target row in public.approved_components (CPN can repeat across rows).">
                        <span>AML row</span>
                      </HoverHintContent>
                    </th>
                    <th className="px-3 py-2">CPN</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Manufacturer</th>
                    <th className="px-3 py-2">MPN</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Detail</th>
                    <th className="px-3 py-2 text-right">Resolution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingRequests.map((requestRow) => (
                    <tr key={requestRow.id}>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                        {new Date(requestRow.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {requestRow.requester_display_name !== requestRow.requested_by ? (
                          <HoverHintContent hoverHint={requestRow.requested_by}>
                            <span>{requestRow.requester_display_name}</span>
                          </HoverHintContent>
                        ) : (
                          requestRow.requester_display_name
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-800">
                        {requestRow.approved_component_id != null ? requestRow.approved_component_id : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link
                          className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-900"
                          to={amlCpnDetailPath(requestRow.cpn)}
                        >
                          {requestRow.cpn}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                        {requestRow.category ?? "—"}
                      </td>
                      <td className="max-w-md min-w-[10rem] whitespace-normal px-3 py-2 text-slate-700">
                        {requestRow.description ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{requestRow.manufacturer_name ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {requestRow.mpn ? requestRow.mpn : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{amlChangeRequestActionLabel(requestRow.action)}</td>
                      <td className="max-w-xs whitespace-normal px-3 py-2 text-slate-700">
                        {summarizeAmlChangeRequestAction(requestRow.action, requestRow.payload)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <ReadOnlyHoverLayer
                          active={!hubMrpAdminAllowed}
                          hoverHint={mrpAmlResolutionTitle}
                          layout="inline"
                        >
                          <span className="inline-flex flex-wrap justify-end gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={requestBusy || !hubMrpAdminAllowed}
                              onClick={() => {
                                if (!hubMrpAdminAllowed) return;
                                approveRequestMutation.mutate(requestRow.id);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={requestBusy || !hubMrpAdminAllowed}
                              onClick={() => {
                                if (!hubMrpAdminAllowed) return;
                                const resolution_notes =
                                  window.prompt("Optional reason for rejection (or leave blank)") ?? "";
                                rejectRequestMutation.mutate({
                                  requestId: requestRow.id,
                                  resolution_notes: resolution_notes.trim() || null,
                                });
                              }}
                            >
                              Reject
                            </Button>
                          </span>
                        </ReadOnlyHoverLayer>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {approveRequestMutation.isError ? (
            <p className="text-sm text-red-600">
              {approveRequestMutation.error instanceof Error
                ? approveRequestMutation.error.message
                : "Approve failed."}
            </p>
          ) : null}
          {rejectRequestMutation.isError ? (
            <p className="text-sm text-red-600">
              {rejectRequestMutation.error instanceof Error
                ? rejectRequestMutation.error.message
                : "Reject failed."}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 border-b border-slate-100 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <span>
              Entries{" "}
              {totalCpns > 0 ? (
                <span className="font-normal text-slate-600">
                  (CPN {rangeStart}–{rangeEnd} of {totalCpns})
                </span>
              ) : (
                <span className="font-normal text-slate-600">(0 CPNs)</span>
              )}
            </span>
            <HoverHintWithGlyph hoverHint={AML_ENTRIES_SECTION_HINT} />
          </CardTitle>
          {totalPages > 1 ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={page <= 1 || amlQuery.isFetching}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || amlQuery.isFetching}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="max-w-md"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search CPN, description, category, variant, manufacturer, MPN, source, notes…"
            />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
              />
              <HoverHintContent hoverHint={AML_SHOW_INACTIVE_HINT}>
                <span className="cursor-help border-b border-dotted border-slate-400">Show inactive</span>
              </HoverHintContent>
            </label>
            {amlQuery.isFetching ? (
              <Loader2
                className="h-4 w-4 shrink-0 animate-spin text-slate-500"
                aria-label="Searching approved material list"
              />
            ) : null}
          </div>

          <AmlEntriesTable
            rows={rows}
            groupedByCpn
            linkCpnToDetail
            skuHoverHints={skuHoverHints}
            isLoading={listLoading}
            emptyMessage={emptyMessage}
            requestBusy={requestBusy}
            hubWritesAllowed={hubWritesAllowed}
            hubReadOnlyTitle={mrpReadOnlyTitle}
            onEditNotes={(row) => {
              setNotesRequestDraft(row.notes ?? "");
              setNotesRequestRow(row);
            }}
            onRequestActivate={(row) => {
              amlRequestMutation.mutate({ id: row.id, body: { kind: "activate" } });
            }}
            onRequestDeactivate={(row) => {
              amlRequestMutation.mutate({ id: row.id, body: { kind: "deactivate" } });
            }}
            onRequestDelete={(row) => {
              amlRequestMutation.mutate({ id: row.id, body: { kind: "delete" } });
            }}
          />
          {amlRequestMutation.isError && !notesRequestRow ? (
            <p className="text-sm text-red-600">
              {amlRequestMutation.error instanceof Error
                ? amlRequestMutation.error.message
                : "Could not submit request."}
            </p>
          ) : null}
        </CardBody>
      </Card>

    </div>
  );
}
